import React, {useMemo, useState} from 'react';
import type {List} from 'immutable';

import {
  Badge,
  Button,
  Heading,
  Icon,
  IconButton,
  SearchInput,
  SegmentedControl,
  WithTooltip
} from '@albert-io/atomic';
import type {ClassroomModelV1} from '@albert-io/models';
import HardCapWarningBanner from 'client/components/HardCapWarningBanner/HardCapWarningBanner.react';

import {InviteMenu} from '../InviteMenu';
import {EnrolledList} from './EnrolledList';
import {InvitedList} from './InvitedList';
import type {RosterClassroom, RosterInvitation, RosterStudent} from './usePeopleRoster';

export type StudentSegment = 'enrolled' | 'invited';

const SEGMENTS: {value: StudentSegment; label: string}[] = [
  {value: 'enrolled', label: 'Enrolled'},
  {value: 'invited', label: 'Invited'}
];

const formatDomains = (domains: string[]): string => {
  if (domains.length <= 1) {
    return domains[0] ?? '';
  }
  if (domains.length === 2) {
    return `${domains[0]} and ${domains[1]}`;
  }
  return `${domains.slice(0, -1).join(', ')}, and ${domains[domains.length - 1]}`;
};

interface Props {
  classroom: RosterClassroom;
  students: List<RosterStudent>;
  invitations: List<RosterInvitation>;
  onShowModal: (modal: string) => void;
  segment: StudentSegment;
  onSegmentChange: (segment: StudentSegment) => void;
}

export const StudentsTab = ({
  classroom,
  students,
  invitations,
  onShowModal,
  segment,
  onSegmentChange
}: Props) => {
  const [search, setSearch] = useState('');
  const [isSearchOpen, setIsSearchOpen] = useState(false);

  // getSchool() can return a stub without its methods when the school_v5 relationship isn't loaded
  // (a shared or refetching interest); treat that as no school.
  const rawSchool = classroom.getSchool();
  const school = typeof rawSchool?.getEmailDomains === 'function' ? rawSchool : null;
  const showSeatBanner = classroom.isLicensed() && school?.isHardCap();
  const isActiveSegmentEmpty = segment === 'enrolled' ? students.isEmpty() : invitations.isEmpty();

  const hasManagedStudents = useMemo(
    () => students.toArray().some((student) => student.getMeta().isManagedUser()),
    [students]
  );

  const managedTooltip = useMemo(() => {
    // school is nullable, and the memo body runs every render — guard here, not at the render site.
    if (!school) {
      return null;
    }
    const domains = (school.getEmailDomains()?.toArray() ?? [])
      .map((domain) => domain.getName())
      .filter(Boolean);
    return domains.length ? (
      <>
        Accounts ending in <strong>{formatDomains(domains)}</strong> are managed by{' '}
        {school.getName()}
      </>
    ) : (
      `Managed by ${school.getName()}`
    );
  }, [school]);

  const handleSegment = (value: string | number) => {
    onSegmentChange(value as StudentSegment);
    setSearch('');
    setIsSearchOpen(false);
  };

  return (
    <section className='flex flex-col'>
      {showSeatBanner && (
        <HardCapWarningBanner
          className='-mx-x4'
          seatsRemaining={classroom.getMeta().getCountOfSchoolLicenseSeatsRemaining()}
        />
      )}
      <div className='gap-x3 pt-x4 flex flex-col'>
        <Heading as='h2' className='m-x0' size='s'>
          Students
        </Heading>
        <div className='gap-x2 flex flex-wrap items-center justify-between'>
          <SegmentedControl
            onSelect={handleSegment}
            options={SEGMENTS}
            selectedIdx={SEGMENTS.findIndex((s) => s.value === segment)}
          />
          {!isActiveSegmentEmpty && (
            <div className='gap-x1 flex flex-wrap items-center'>
              {segment === 'enrolled' && hasManagedStudents && (
                <WithTooltip content={managedTooltip} enabled={Boolean(managedTooltip)} focusable>
                  <Badge emphasis='subtle'>
                    <span className='gap-x1 content-primary inline-flex items-center'>
                      <Icon color='secondary' icon={['far', 'school']} size='inherit' />
                      Managed by school
                      <Icon
                        color='secondary'
                        icon='circle-info'
                        iconStyle='regular'
                        size='inherit'
                      />
                    </span>
                  </Badge>
                </WithTooltip>
              )}
              {isSearchOpen ? (
                <div
                  onBlur={(e) => {
                    if (!search && !e.currentTarget.contains(e.relatedTarget as Node | null)) {
                      setIsSearchOpen(false);
                    }
                  }}
                >
                  <SearchInput
                    autoFocus
                    className='w-[256px]'
                    placeholder={
                      segment === 'invited' ? 'Search by email' : 'Search by name or email'
                    }
                    rectangle
                    value={search}
                    onChange={(_e, value) => setSearch(value)}
                    onReset={() => {
                      setSearch('');
                      setIsSearchOpen(false);
                    }}
                  />
                </div>
              ) : (
                <IconButton
                  color='secondary'
                  icon='magnifying-glass'
                  label='Search students'
                  roundness='square'
                  variant='outlined'
                  onClick={() => setIsSearchOpen(true)}
                />
              )}
              {segment === 'invited' && (
                <Button onClick={() => onShowModal('bulkResend')}>
                  <Icon className='mr-x1' icon='paper-plane' iconStyle='regular' />
                  Resend Invites
                </Button>
              )}
              <InviteMenu
                classroom={classroom as unknown as ClassroomModelV1}
                classroomId={classroom.getId()}
                trigger={(triggerProps) => (
                  <Button {...triggerProps}>
                    <Icon className='mr-x1' icon='user-plus' iconStyle='regular' />
                    Invite Students
                  </Button>
                )}
              />
            </div>
          )}
        </div>
        {segment === 'enrolled' ? (
          <EnrolledList
            classroom={classroom}
            invitations={invitations}
            onShowModal={onShowModal}
            search={search}
            students={students}
          />
        ) : (
          <InvitedList
            classroom={classroom}
            invitations={invitations}
            onShowModal={onShowModal}
            search={search}
            students={students}
          />
        )}
      </div>
    </section>
  );
};
